import { firstLabelApi, useGetFirstLabelRunsQuery } from './api/firstLabelApi';

// Statuses meaning a run is still working (mirrors backend ACTIVE_STATUSES).
export const FIRST_LABEL_ACTIVE_STATUSES = new Set([
  'queued', 'identity', 'research', 'dataroom', 'slides',
  'layout_qa', 'content_qa', 'verify', 'export',
]);

// A report update (private documents folded into a finished report) is
// working while in one of these (mirrors backend ACTIVE_UPDATE_STATUSES).
export const REPORT_UPDATE_ACTIVE_STATUSES = new Set(['queued', 'running']);

// User-facing phases shown in the progress gate. Deliberately generic (no
// internal pipeline details) and monotonic: several backend statuses map to
// one visible step so the stepper never appears to move backwards.
// `label` and `description` are i18n keys, resolved with `t()` where shown.
export const FIRST_LABEL_STAGES = [
  {
    key: 'research',
    label: 'dataroom.firstLabel.stages.researchLabel',
    description: 'dataroom.firstLabel.stages.researchDescription',
    statuses: ['queued', 'identity', 'research'],
  },
  {
    key: 'build',
    label: 'dataroom.firstLabel.stages.buildLabel',
    description: 'dataroom.firstLabel.stages.buildDescription',
    statuses: ['slides', 'dataroom', 'content_qa', 'layout_qa', 'verify'],
  },
  {
    key: 'publish',
    label: 'dataroom.firstLabel.stages.publishLabel',
    description: 'dataroom.firstLabel.stages.publishDescription',
    statuses: ['export'],
  },
];

export function stageIndexForStatus(status) {
  const index = FIRST_LABEL_STAGES.findIndex((stage) => stage.statuses.includes(status));
  return index === -1 ? 0 : index;
}

/**
 * Latest First Label run for a project, polled only while it is active.
 *
 * A finished report never changes status on its own, so polling it forever
 * was pure load: one open tab on a ready Cambon deal sent 893 requests in
 * 2.5 hours (2026-09-21). The cached result decides whether to poll; a new
 * run invalidates the list (createFirstLabelRun), which refetches and turns
 * polling back on.
 *
 * A report update does not change the run's status, so polling also runs
 * while the latest update is working, until it finishes.
 *
 * `isBlocking` is true while the run is active, or when it failed BEFORE
 * producing anything: the workspace is useless only while no dataroom
 * exists. A run that failed the quality bar after exporting its deliverables
 * (artifacts present) must NOT lock the user out of a working dataroom, so
 * it surfaces as a sidebar indicator with a retry instead of the gate.
 */
export function useFirstLabelRun(projectUuid) {
  const { data: cachedRuns } = firstLabelApi.endpoints.getFirstLabelRuns.useQueryState(projectUuid, {
    skip: !projectUuid,
  });
  const cachedRun = cachedRuns?.[0];
  const pollWhileActive = FIRST_LABEL_ACTIVE_STATUSES.has(cachedRun?.status)
    || REPORT_UPDATE_ACTIVE_STATUSES.has(cachedRun?.private_update?.latest?.status);
  const { data: runs = [] } = useGetFirstLabelRunsQuery(projectUuid, {
    skip: !projectUuid,
    pollingInterval: pollWhileActive ? 5000 : 0,
    skipPollingIfUnfocused: true,
  });
  const run = runs[0] || null;
  const isActive = Boolean(run && FIRST_LABEL_ACTIVE_STATUSES.has(run.status));
  const hasDeliverables = Boolean(run?.artifacts?.length);
  const isBlocking = Boolean(run && (isActive || (run.status === 'failed' && !hasDeliverables)));
  const latestUpdate = run?.private_update?.latest || null;
  const isUpdating = REPORT_UPDATE_ACTIVE_STATUSES.has(latestUpdate?.status);
  const pendingDocuments = run?.private_update?.pending_documents || 0;
  return { run, isActive, isBlocking, hasDeliverables, isUpdating, pendingDocuments, latestUpdate };
}
